// ============================================================
// API client core — shared by web (frontend/) and mobile (mobile/).
//
// Generalised from frontend/src/api/client.js. The web app and the mobile
// app differ only in (a) where the bearer token comes from and (b) the base
// URL. Both are injected via createApiClient(), so the request/retry/error/
// toCamel logic lives in exactly one place.
//
//   Web:    createApiClient({ baseUrl: "/api",            getToken: msalBrowserToken })
//   Mobile: createApiClient({ baseUrl: "https://<host>/api", getToken: msalNativeToken })
// ============================================================

// Default per-request timeout. Most endpoints answer well within this; slow
// endpoints (e.g. the Harvi chat, which can make several Claude calls plus a
// batch of DB writes on one turn) pass a larger `timeout` via RequestOptions.
import { createSseParser, SseEvent } from "./sse";

const TIMEOUT_MS = 10000;
const MAX_RETRIES = 2;

// ── toCamel ──────────────────────────────────────────────────────────────────
// Recursively lower-cases the first character of every object key
// (SQL Server PascalCase columns → JS camelCase).
export const toCamel = (o: any): any => {
  if (Array.isArray(o)) return o.map(toCamel);
  if (o && typeof o === "object") {
    return Object.fromEntries(
      Object.entries(o).map(([k, v]) => [k.charAt(0).toLowerCase() + k.slice(1), toCamel(v)])
    );
  }
  return o;
};

async function fetchWithTimeout(url: string, options: RequestInit, timeoutMs: number): Promise<Response> {
  const ctrl = new AbortController();
  let timedOut = false;
  const timer = setTimeout(() => {
    timedOut = true;
    ctrl.abort();
  }, timeoutMs);
  try {
    return await fetch(url, { ...options, signal: ctrl.signal });
  } catch (e) {
    // Turn our own abort into a clear, self-describing timeout error (the raw
    // abort surfaces as opaque messages like "Fetch request has been canceled"
    // on React Native) and tag it so the retry layer can treat it distinctly.
    if (timedOut) {
      const err: any = new Error(`Request timed out after ${Math.round(timeoutMs / 1000)}s. Please try again.`);
      err.isTimeout = true;
      throw err;
    }
    // Any other fetch() rejection is a network-level failure — offline, DNS
    // lookup failed, connection refused/reset, TLS. These surface as opaque
    // platform strings ("fetch failed: A server with the specified hostname
    // could not be found" on iOS, "Network request failed" on Android,
    // "Failed to fetch" in browsers). Normalise to one clear, friendly message
    // so every caller's error UI reads sensibly; tag it so the retry layer still
    // treats it as retryable and callers can branch on `isNetwork` if needed.
    const err: any = new Error("You appear to be offline. Check your internet connection and try again.");
    err.isNetwork = true;
    err.cause = e;
    throw err;
  } finally {
    clearTimeout(timer);
  }
}

// Retries only genuine network-level failures (connection refused/reset, DNS,
// offline) with exponential backoff. Does NOT retry:
//   • HTTP error responses — those are deterministic (handled in `handle`).
//   • Timeouts — a timeout means the request already exceeded its full budget;
//     retrying just burns another full budget, and for non-idempotent mutations
//     the aborted request may still be running server-side, so a retry risks
//     duplicate writes. Surface the timeout immediately instead.
async function fetchRetry(url: string, options: RequestInit, timeoutMs: number, retries = MAX_RETRIES): Promise<Response> {
  for (let i = 0; i <= retries; i++) {
    try {
      return await fetchWithTimeout(url, options, timeoutMs);
    } catch (e) {
      if ((e as any)?.isTimeout || i === retries) throw e;
      await new Promise((r) => setTimeout(r, Math.pow(2, i) * 500));
    }
  }
  // Unreachable — the loop either returns or throws.
  throw new Error("fetchRetry: exhausted retries");
}

function errorMessage(status: number, body: any): string {
  if (status === 401) return "Not authorized — please sign in again.";
  if (status === 403) return "You don't have permission to perform this action.";
  if (status === 404) return body.detail || body.error || "Record not found.";
  if (status >= 500) return body.detail || body.error || `Server error (${status}). Please try again.`;
  return body.detail || body.error || `Request failed (${status})`;
}

export interface ApiClientOptions {
  /** Base URL prefix, e.g. "/api" (web) or "https://harvus.azurewebsites.net/api" (mobile). No trailing slash. */
  baseUrl: string;
  /** Returns the current bearer token, or null if unauthenticated. */
  getToken: () => Promise<string | null>;
}

/** Per-request overrides. */
export interface RequestOptions {
  /** Timeout in ms for this request. Defaults to TIMEOUT_MS (10s). */
  timeout?: number;
}

/** Per-request options for a streamed POST. */
export interface StreamOptions {
  /** Called for each SSE frame, in order, as it arrives. */
  onEvent: (event: SseEvent) => void;
  /** Abort the stream early (the user navigated away, or pressed stop). */
  signal?: AbortSignal;
}

export interface ApiClient {
  get: <T = any>(path: string, opts?: RequestOptions) => Promise<T>;
  post: <T = any>(path: string, body: unknown, opts?: RequestOptions) => Promise<T>;
  put: <T = any>(path: string, body: unknown, opts?: RequestOptions) => Promise<T>;
  del: <T = any>(path: string, opts?: RequestOptions) => Promise<T | null>;
  /**
   * POST that reads a Server-Sent Events response, calling `onEvent` per frame.
   *
   * Deliberately NOT built on fetchRetry: a retry replays a half-written answer,
   * and there is no timeout because a streamed turn is long by design — the
   * whole point is that the first token arrives quickly. Cancel with `signal`.
   *
   * Returns { streamed, data }. When the server answers with plain JSON instead
   * of a stream — streaming switched off, or a proxy that would not carry it —
   * `streamed` is false and `data` is that already-parsed response. It is NOT a
   * signal to try again: the turn has already happened server-side, and
   * re-posting would ask the question a second time.
   */
  postStream: (path: string, body: unknown, opts: StreamOptions) => Promise<{ streamed: boolean; data: any }>;
}

export function createApiClient({ baseUrl, getToken }: ApiClientOptions): ApiClient {
  const url = (path: string) => `${baseUrl}/${path}`;

  const authHeaders = async (extra?: Record<string, string>) => {
    const token = await getToken();
    return {
      ...(extra || {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
  };

  const handle = async (r: Response) => {
    if (!r.ok) {
      const body = await r.json().catch(() => ({}));
      // Attach the status code and parsed body so callers can branch on them
      // (e.g. a 409 with a structured `needs` payload → a confirm-and-resubmit
      // flow). Backward-compatible: `err.message` is unchanged.
      const err: any = new Error(errorMessage(r.status, body));
      err.status = r.status;
      err.body = body;
      throw err;
    }
    if (r.status === 204) return null;
    return toCamel(await r.json());
  };

  return {
    get: async (path, opts) => {
      const headers = await authHeaders();
      return handle(await fetchRetry(url(path), { headers }, opts?.timeout ?? TIMEOUT_MS));
    },
    post: async (path, body, opts) => {
      const headers = await authHeaders({ "Content-Type": "application/json" });
      return handle(await fetchRetry(url(path), { method: "POST", headers, body: JSON.stringify(body) }, opts?.timeout ?? TIMEOUT_MS));
    },
    put: async (path, body, opts) => {
      const headers = await authHeaders({ "Content-Type": "application/json" });
      return handle(await fetchRetry(url(path), { method: "PUT", headers, body: JSON.stringify(body) }, opts?.timeout ?? TIMEOUT_MS));
    },
    del: async (path, opts) => {
      const headers = await authHeaders();
      return handle(await fetchRetry(url(path), { method: "DELETE", headers }, opts?.timeout ?? TIMEOUT_MS));
    },
    postStream: async (path, body, opts) => {
      const headers = await authHeaders({
        "Content-Type": "application/json",
        Accept: "text/event-stream",
      });
      const response = await fetch(url(path), {
        method: "POST",
        headers,
        body: JSON.stringify(body),
        ...(opts?.signal ? { signal: opts.signal } : {}),
      });

      // An error response is JSON, not a stream — hand it to the normal error
      // path so a failed streamed turn reads exactly like a failed buffered one.
      if (!response.ok) await handle(response);

      // The server may answer a stream request with plain JSON. The turn still
      // happened, so return its body rather than discarding it.
      const contentType = response.headers?.get?.("content-type") || "";
      if (!/text\/event-stream/i.test(contentType)) {
        return { streamed: false, data: await handle(response) };
      }

      const parser = createSseParser();
      const stream: any = (response as any).body;

      // No readable body (an older React Native fetch, a polyfill): take the
      // whole text and parse it in one go. The frames still arrive in order and
      // the answer is still correct — it just all lands at the end.
      if (!stream || typeof stream.getReader !== "function") {
        const text = await response.text();
        for (const event of parser.push(text)) opts.onEvent(event);
        for (const event of parser.flush()) opts.onEvent(event);
        return { streamed: true, data: null };
      }

      const reader = stream.getReader();
      const decoder = new TextDecoder();
      try {
        for (;;) {
          const { done, value } = await reader.read();
          if (done) break;
          for (const event of parser.push(decoder.decode(value, { stream: true }))) {
            opts.onEvent(event);
          }
        }
        for (const event of parser.flush()) opts.onEvent(event);
      } finally {
        try { reader.releaseLock(); } catch { /* already released */ }
      }
      return { streamed: true, data: null };
    },
  };
}
